import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { select, Store } from '@ngrx/store'
import * as R from 'ramda'

import { CheckInStatus, Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { ChangeAirBnBResponse, DeleteInquiry, SendGuestLogin } from '@tv3/store/inquiry/inquiry.actions'
import * as fromRoot from '@tv3/store/state'
import { ChannelNameTokens, ConfirmDialogService, Toaster, Destroyable, isSomething } from '@tokeet/tv3-platform'
import {
  ArchiveInquiry,
  FollowupInquiry,
  MarkPaidInquiry,
  MarkUnpaidInquiry,
  NoFollowupInquiry,
  ReadInquiry,
  SetGuestPortalAccessible,
  UnArchiveInquiry,
  UnLinkInquiry,
  UnReadInquiry,
  UpdateCheckinStatus,
  UpdateCheckoutStatus,
} from '@tv3/store/inquiry/inquiry-fields.actions'
import { InquirySharedDialogsService } from '@tv3/containers/inquiries/dialogs/inquiry-shared-dialogs.service'
import { PrintBookingPreviewDialogService } from '@tv3/shared/dialogs/print-booking-preview/print-booking-preview-dialog.service'
import { PaymentGateway } from '@tokeet-frontend/gateways'
import { selectConnectionByLinkedRentalId } from '@tv3/store/connection/connection.selectors'
import { catchError, debounceTime, filter, map, shareReplay, switchMap, take } from 'rxjs/operators'
import { ImportBookingFromABBV2Connection } from '@tv3/store/connection/connection.actions'
import { WebsiteService } from '@tv3/store/website/website.service'
import { BehaviorSubject, of, Subject } from 'rxjs'
import { isImportedFromABBV2 } from '@tokeet-frontend/inquiry'

@Component({
  selector: 'app-inquiry-action-button',
  templateUrl: './inquiry-action-button.component.html',
  styleUrls: ['./inquiry-action-button.component.scss'],
})
export class InquiryActionButtonComponent implements OnChanges {
  @Input() inquiry: Inquiry
  @Input() payment: PaymentGateway

  @Input() options: string[]
  @Input() circleButton = false

  @Output() deleted = new EventEmitter<Inquiry>()
  @Output() archived = new EventEmitter<Inquiry>()

  checkInStatus = CheckInStatus
  isAirbnbBooking = false

  rentalId$ = new BehaviorSubject('')
  guestPortalDomain$ = this.rentalId$.pipe(
    filter(isSomething),
    debounceTime(1000),
    switchMap((rentalId) => this.websiteService.getGuestPortalForRental(rentalId)),
    map((res) => res.domain),
    shareReplay(1),
    catchError((err) => of(null))
  )

  constructor(
    private store: Store<fromRoot.State>,
    private toaster: Toaster,
    private confirmDialog: ConfirmDialogService,
    private dialogs: InquirySharedDialogsService,
    private websiteService: WebsiteService,
    private printBookingService: PrintBookingPreviewDialogService
  ) {}

  showOption(options: string[], ...allowedOptions: string[]) {
    if (!options || !options.length) return true

    for (let i = 0; i < allowedOptions.length; i++) {
      if (options.includes(allowedOptions[i])) return true
    }

    return false
  }

  ngOnChanges(changes: SimpleChanges) {
    this.inquiry = R.pathOr(this.inquiry, ['inquiry', 'currentValue'], changes)
    this.rentalId$.next(this.inquiry.rentalId)
    this.payment = R.pathOr(this.payment, ['payment', 'currentValue'], changes)
    this.isAirbnbBooking = isImportedFromABBV2(this.inquiry)
  }

  onAddBillingDetails() {
    this.dialogs.openAddBilling(this.inquiry)
  }

  onViewBillingDetails() {
    this.dialogs.openViewBilling(this.inquiry, this.payment)
  }

  onManageTags() {
    this.dialogs.openTags(this.inquiry)
  }

  onPrint() {
    this.printBookingService.open({ bookingId: this.inquiry.id })
  }

  onFollowUp() {
    this.store.dispatch(FollowupInquiry({ inquiry: this.inquiry }))
  }

  onNoFollowUp() {
    this.store.dispatch(NoFollowupInquiry({ inquiry: this.inquiry }))
  }

  onRead() {
    this.store.dispatch(ReadInquiry({ inquiry: this.inquiry }))
  }

  onUnRead() {
    this.store.dispatch(UnReadInquiry({ inquiry: this.inquiry }))
  }

  onPaid() {
    this.store.dispatch(MarkPaidInquiry({ inquiry: this.inquiry }))
  }

  onUnPaid() {
    this.store.dispatch(MarkUnpaidInquiry({ inquiry: this.inquiry }))
  }

  onCheckedIn() {
    this.store.dispatch(UpdateCheckinStatus({ inquiry: this.inquiry, status: 1 }))
  }

  onCheckedOut() {
    this.store.dispatch(UpdateCheckoutStatus({ inquiry: this.inquiry, status: 1 }))
  }

  onArchive() {
    const name = this.inquiry.booked ? 'Booking' : 'Inquiry'
    this.confirmDialog
      .confirm({
        title: `Archive ${name}`,
        body: `Are you sure you want to archive this ${name}?`,
      })
      .subscribe(() => {
        this.store.dispatch(ArchiveInquiry({ inquiry: this.inquiry }))
        this.archived.emit(this.inquiry)
      })
  }

  onUnArchive() {
    this.store.dispatch(UnArchiveInquiry({ inquiry: this.inquiry }))
  }

  onDelete() {
    this.confirmDialog
      .confirmExtra({
        title: 'Delete this inquiry?',
        body: 'Are you sure you want to delete this inquiry? <br>Once this is deleted this action cannot be undone. Please note that deleting inquiries will not result in cancellation of the booking on the originating channel.',
        extra: 'Also delete all guests associated with this inquiry.',
      })
      .subscribe((result) => {
        this.store.dispatch(DeleteInquiry({ inquiry: this.inquiry, deleteGuest: result.isChecked }))
        this.deleted.emit(this.inquiry)
      })
  }

  onSendGuestLogin() {
    this.store.dispatch(SendGuestLogin({ inquiry: this.inquiry }))
  }

  onImport() {
    const booking = this.inquiry
    this.store
      .pipe(select(selectConnectionByLinkedRentalId(booking.rentalId, ChannelNameTokens.AirBnBV2API)), take(1))
      .subscribe((connections) => {
        const conn = R.head(connections)
        if (conn) {
          this.store.dispatch(
            ImportBookingFromABBV2Connection({ inquiryId: booking.inquiryId, channelId: conn.channelId, data: conn })
          )
        } else {
          this.toaster.warning('The associated rental is not linked to any airbnb listing.', 'Unable to import')
        }
      })
  }

  onUnLink() {
    this.confirmDialog
      .confirm({
        title: 'Unlink inquiries?',
        body: 'This inquiry is currently linked with 1 other inquiries. Are you sure you want to remove the links to these inquiries?  Once the links are removed messages, invoices, notes, and flight information from the linked inquiries will no longer be displayed in this inquiry. Please note that information from this inquiry may still be displayed in other inquiries.',
      })
      .subscribe(() => {
        this.store.dispatch(UnLinkInquiry({ inquiry: this.inquiry }))
      })
  }

  onClone() {
    this.dialogs.cloneInquiry(this.inquiry)
  }

  onSetGuestPortalAccessible() {
    this.store.dispatch(
      SetGuestPortalAccessible({
        inquiryId: this.inquiry.id,
        rentalId: this.inquiry.rentalId,
        disabled: !this.inquiry.disableGuestPortal,
      })
    )
  }

  onPreApprove() {
    this.store.dispatch(ChangeAirBnBResponse({ inquiry: this.inquiry, status: 'preapproved' }))
  }

  onDeny() {
    this.store.dispatch(ChangeAirBnBResponse({ inquiry: this.inquiry, status: 'denied' }))
  }
}
